/* Fouroz collection page behaviour (Claude Design, Oct 2026). Quick add itself is handled by fz.js. */
(() => {
  const STORE_KEY = 'fz-grid-density';

  // Keep the sticky toolbar just under the sticky header
  function trackHeader() {
    const header = document.querySelector('.fz-header-section');
    if (!header || window.fzHeaderTracked) return;
    window.fzHeaderTracked = true;
    const set = () => document.documentElement.style.setProperty('--fz-sticky-top', header.offsetHeight + 'px');
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(header);
  }

  function initCollection(root) {
    if (root.dataset.fzReady) return;
    root.dataset.fzReady = '1';
    const grid = root.querySelector('[data-fz-grid]');

    /* Grid density, remembered per visitor */
    const density = root.querySelectorAll('[data-fz-density]');
    const setDensity = (mode, save) => {
      if (!grid) return;
      grid.classList.toggle('fz-grid--compact', mode === 'compact');
      grid.classList.toggle('fz-grid--large', mode === 'large');
      density.forEach(b => b.setAttribute('aria-pressed', b.dataset.fzDensity === mode ? 'true' : 'false'));
      if (save) { try { localStorage.setItem(STORE_KEY, mode); } catch (e) {} }
    };
    try { const saved = localStorage.getItem(STORE_KEY); if (saved) setDensity(saved, false); } catch (e) {}
    density.forEach(b => b.addEventListener('click', () => setDensity(b.dataset.fzDensity, true)));

    /* Sort submits straight away */
    const sort = root.querySelector('[data-fz-sort]');
    if (sort) sort.addEventListener('change', () => sort.form.submit());

    /* Filter drawer */
    const drawer = root.querySelector('[data-fz-filters]');
    const opener = root.querySelector('[data-fz-filters-open]');
    if (drawer && opener) {
      const panel = drawer.querySelector('[role="dialog"]');
      const focusables = () => [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary, select')].filter(el => el.offsetParent !== null);
      const open = () => {
        drawer.hidden = false;
        opener.setAttribute('aria-expanded', 'true');
        document.documentElement.classList.add('fz-lock');
        (focusables()[0] || panel).focus();
      };
      const close = () => {
        drawer.hidden = true;
        opener.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('fz-lock');
        opener.focus();
      };
      opener.addEventListener('click', open);
      drawer.querySelectorAll('[data-fz-filters-close]').forEach(el => el.addEventListener('click', close));
      drawer.addEventListener('keydown', e => {
        if (e.key === 'Escape') { close(); return; }
        if (e.key !== 'Tab') return;
        const f = focusables();
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    /* Size sheet for quick add */
    const closeSheets = except => root.querySelectorAll('.fz-sheet:not([hidden])').forEach(s => {
      if (s === except) return;
      s.hidden = true;
      const btn = root.querySelector(`[aria-controls="${s.id}"]`);
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
    root.addEventListener('click', e => {
      const openBtn = e.target.closest('[data-fz-sheet-open]');
      if (openBtn) {
        const sheet = document.getElementById(openBtn.getAttribute('aria-controls'));
        closeSheets(sheet);
        sheet.hidden = false;
        openBtn.setAttribute('aria-expanded', 'true');
        const first = sheet.querySelector('.fz-sheet__size:not([disabled])');
        if (first) first.focus();
        return;
      }
      const closeBtn = e.target.closest('[data-fz-sheet-close]');
      if (closeBtn) {
        const sheet = closeBtn.closest('.fz-sheet');
        sheet.hidden = true;
        const btn = root.querySelector(`[aria-controls="${sheet.id}"]`);
        if (btn) { btn.setAttribute('aria-expanded', 'false'); btn.focus(); }
        return;
      }
      // A size was picked: fz.js adds it to the bag; close the sheet shortly after
      const size = e.target.closest('.fz-sheet__size');
      if (size) setTimeout(() => closeSheets(), 400);
      else if (!e.target.closest('.fz-sheet')) closeSheets();
    });
    root.addEventListener('keydown', e => { if (e.key === 'Escape') closeSheets(); });

    /* Load more: fetch the next page of this section and append it */
    const more = root.querySelector('[data-fz-more]');
    const moreBtn = more && more.querySelector('[data-fz-more-btn]');
    if (more && moreBtn && grid) {
      moreBtn.addEventListener('click', async e => {
        e.preventDefault();
        const next = Number(more.dataset.page) + 1;
        const url = new URL(window.location.href);
        url.searchParams.set('page', next);
        url.searchParams.set('section_id', root.dataset.sectionId);
        moreBtn.setAttribute('aria-busy', 'true');
        try {
          const html = await (await fetch(url.toString())).text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cards = doc.querySelectorAll('[data-fz-grid] [data-fz-card]');
          if (!cards.length) throw new Error('empty');
          cards.forEach(c => grid.appendChild(document.importNode(c, true)));
          const shown = Math.min(Number(more.dataset.shown) + cards.length, Number(more.dataset.total));
          more.dataset.page = next;
          more.dataset.shown = shown;
          more.querySelector('[data-fz-more-label]').textContent = `Showing ${shown} of ${more.dataset.total}`;
          more.querySelector('[data-fz-more-bar]').style.width = Math.round(shown / Number(more.dataset.total) * 100) + '%';
          cards[0] && grid.querySelectorAll('[data-fz-card]')[shown - cards.length]?.querySelector('.fz-card__title')?.focus({ preventScroll: true });
          if (next >= Number(more.dataset.pages)) moreBtn.remove();
        } catch (err) {
          window.location.href = moreBtn.href; // fall back to the next page
        } finally {
          moreBtn.removeAttribute('aria-busy');
        }
      });
    }
  }

  function init(scope = document) {
    trackHeader();
    scope.querySelectorAll('[data-fz-collection]').forEach(initCollection);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', e => init(e.target));
})();
